import { useMemo } from 'react';
import { StyleSheet } from 'react-native';
import { useScale } from '../../../hooks/useScale';
import { useColors } from '../../../hooks/useColors';
import theme from '../../../common/theme';

const SetModifierStyleModalStyles = () => {
  const { scale } = useScale();
  const { colors } = useColors();

  return useMemo(
    () =>
      StyleSheet.create({
        background: {
          flex: 1,
          flexDirection: 'row',
          justifyContent: 'flex-end',
          backgroundColor: theme.colors.modalBg,
        },
        panel: {
          width: scale(420),
          height: '100%',
          backgroundColor: colors.popupBg,
        },
        header: {
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: scale(24),
          paddingBottom: scale(16),
        },
        scrollArea: {
          flex: 1,
        },
        scrollContent: {
          paddingTop: scale(8),
          paddingHorizontal: scale(24),
          paddingBottom: scale(24),
          rowGap: scale(24),
        },
        headerActions: {
          flexDirection: 'row',
          alignItems: 'center',
          columnGap: scale(10),
        },
        headerIconButton: {
          width: scale(32),
          height: scale(32),
          borderRadius: theme.radius.s,
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: colors.inputBackground,
        },
        title: {
          color: colors.text,
          fontSize: scale(20),
          fontFamily: theme.fonts.semibold,
        },
        fieldLabelRow: {
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: scale(8),
        },
        fieldLabel: {
          color: colors.text,
          fontSize: scale(13),
          fontFamily: theme.fonts.medium,
        },
        sectionLabelSpacing: {
          marginBottom: scale(10),
        },
        counterText: {
          color: colors.textLight,
          fontSize: scale(12),
          fontFamily: theme.fonts.regular,
        },
        inputContainer: {
          width: '100%',
          borderRadius: theme.radius.s,
        },
        swatchGrid: {
          flexDirection: 'row',
          flexWrap: 'wrap',
          gap: scale(14),
        },
        swatch: {
          flexBasis: '30%',
          flexGrow: 1,
          minHeight: scale(56),
          flexDirection: 'row',
          alignItems: 'center',
          columnGap: scale(10),
          paddingVertical: scale(12),
          paddingHorizontal: scale(12),
          borderRadius: theme.radius.m,
          borderWidth: 1,
          borderColor: colors.borderColor,
          backgroundColor: colors.inputBackground,
          userSelect: 'none',
        },
        swatchSelected: {
          borderColor: colors.text,
        },
        swatchDot: {
          width: scale(20),
          height: scale(20),
          borderRadius: theme.radius.s,
          flexShrink: 0,
        },
        swatchLabel: {
          color: colors.text,
          fontSize: scale(12),
          lineHeight: scale(16),
          fontFamily: theme.fonts.regular,
          flexShrink: 1,
        },
        noColourRow: {
          marginTop: scale(14),
          flexDirection: 'row',
          alignItems: 'center',
          columnGap: scale(10),
          paddingVertical: scale(12),
          paddingHorizontal: scale(12),
          borderRadius: theme.radius.m,
          borderWidth: 1,
          borderColor: colors.borderColor,
          backgroundColor: colors.inputBackground,
          userSelect: 'none',
        },
        noColourDot: {
          width: scale(20),
          height: scale(20),
          borderRadius: theme.radius.s,
          borderWidth: 1,
          borderColor: colors.textLight,
          overflow: 'hidden',
          alignItems: 'center',
          justifyContent: 'center',
        },
        noColourSlash: {
          width: scale(26),
          height: 1,
          backgroundColor: colors.textLight,
          transform: [{ rotate: '45deg' }],
        },
        noColourTextGroup: {
          rowGap: scale(2),
        },
        noColourLabel: {
          color: colors.text,
          fontSize: scale(12),
          fontFamily: theme.fonts.regular,
        },
        noColourHelper: {
          color: colors.textLight,
          fontSize: scale(11),
          fontFamily: theme.fonts.regular,
        },
        noColourRowDisabled: {
          opacity: 0.4,
        },
        italicToggleRow: {
          flexDirection: 'row',
          alignItems: 'center',
          columnGap: scale(12),
        },
        italicToggle: {
          flexDirection: 'row',
          alignItems: 'center',
          columnGap: scale(6),
          paddingVertical: scale(8),
          paddingHorizontal: scale(14),
          borderRadius: theme.radius.s,
          borderWidth: 1,
          borderColor: colors.borderColor,
          backgroundColor: colors.inputBackground,
          userSelect: 'none',
        },
        italicToggleSelected: {
          borderColor: colors.text,
        },
        italicToggleLabel: {
          color: colors.text,
          fontSize: scale(13),
          fontFamily: theme.fonts.regular,
        },
        helperText: {
          color: colors.textLight,
          fontSize: scale(11),
          fontFamily: theme.fonts.regular,
          lineHeight: scale(15),
        },
        row: {
          flexDirection: 'row',
          alignItems: 'center',
          columnGap: scale(10),
        },
        keywordInput: {
          flex: 1,
        },
        keywordAddButton: {
          height: scale(44),
        },
        matchTypeGroup: {
          flexDirection: 'row',
          borderRadius: theme.radius.s,
          borderWidth: 1,
          borderColor: colors.borderColor,
          backgroundColor: colors.inputBackground,
          overflow: 'hidden',
        },
        matchTypeOption: {
          paddingVertical: scale(8),
          paddingHorizontal: scale(10),
          userSelect: 'none',
        },
        matchTypeOptionSelected: {
          backgroundColor: colors.elevation2,
        },
        matchTypeLabel: {
          color: colors.textLight,
          fontSize: scale(12),
          fontFamily: theme.fonts.regular,
        },
        matchTypeLabelSelected: {
          color: colors.text,
          fontFamily: theme.fonts.medium,
        },
        keywordList: {
          rowGap: scale(8),
        },
        keywordChip: {
          flexDirection: 'row',
          alignItems: 'center',
          flexWrap: 'nowrap',
          columnGap: scale(10),
          paddingVertical: scale(8),
          paddingLeft: scale(14),
          paddingRight: scale(8),
          borderRadius: theme.radius.m,
          backgroundColor: colors.inputBackground,
        },
        keywordChipText: {
          flex: 1,
          flexShrink: 1,
          color: colors.text,
          fontFamily: theme.fonts.medium,
          fontSize: scale(14),
        },
        keywordChipRight: {
          flexShrink: 0,
          flexDirection: 'row',
          alignItems: 'center',
          columnGap: scale(8),
        },
        keywordRemove: {
          width: scale(32),
          height: scale(32),
          borderRadius: theme.radius.s,
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: colors.elevation2,
        },
        errorText: {
          color: theme.colors.red,
          fontSize: scale(12),
          fontFamily: theme.fonts.regular,
        },
        // The preview reuses the real DocketItemStyles so it is visually
        // identical to a live docket; these few overrides only frame it like
        // a single docket sitting on the KDS grid background.
        previewTypeRow: {
          flexDirection: 'row',
          columnGap: scale(8),
          marginBottom: scale(10),
        },
        previewTypeChip: {
          paddingVertical: scale(6),
          paddingHorizontal: scale(12),
          borderRadius: theme.radius.l,
          borderWidth: 1,
          borderColor: colors.borderColor,
          backgroundColor: colors.inputBackground,
          userSelect: 'none',
        },
        previewTypeChipSelected: {
          borderColor: colors.text,
          backgroundColor: colors.elevation2,
        },
        previewTypeChipText: {
          color: colors.textLight,
          fontSize: scale(12),
          fontFamily: theme.fonts.regular,
        },
        previewTypeChipTextSelected: {
          color: colors.text,
          fontFamily: theme.fonts.medium,
        },
        // Outer frame stands in for the KDS grid background behind a docket,
        // so it deliberately uses a different surface than the docket card.
        previewFrame: {
          backgroundColor: colors.elevation0,
          borderRadius: theme.radius.m,
          padding: scale(12),
        },
        // The docket card itself — same elevated surface a real docket tile
        // renders on (white in light mode, raised dark in dark mode).
        previewDocket: {
          backgroundColor: colors.elevation1,
          borderRadius: theme.radius.m,
          overflow: 'hidden',
          paddingHorizontal: scale(6),
          paddingBottom: scale(8),
        },
        previewItemRow: {
          alignItems: 'center',
        },
        previewQty: {
          alignItems: 'center',
          justifyContent: 'center',
        },
        previewModifierMatched: {
          opacity: 1,
        },
        previewModifierItalic: {
          fontStyle: 'italic',
        },
        actions: {
          flexDirection: 'row',
          columnGap: scale(10),
          padding: scale(24),
          borderTopWidth: 1,
          borderTopColor: colors.borderColor,
          backgroundColor: colors.popupBg,
        },
        saveButton: {
          flex: 1,
        },
        cancelButton: {
          width: scale(110),
        },
      }),
    [colors, scale],
  );
};

export default SetModifierStyleModalStyles;
